const _ = require('lodash');
const moment = require('moment');
const { isDeal1BetterThanDeal2 } = require('../entities/Hotel');
const { DEAL_STATUS } = require('../constants/DealConstants');

/**
 * THE DERIVATIONS BEHIND THE ROOM MAPPING 3.0 "CHOOSE YOUR ROOM" SURFACE, shared by web and mobile.
 *
 * Spec: `frontend/ui-specs/hotel-deals-rm3/spec.md` §3. Pure: no i18n, no formatting, no React, no currency
 * conversion and no naming logic — every string returned here is a wire value read as-is.
 *
 * Container = `room_display_group.key`, card = `room_display_group.card_id` WITHIN that key. A container has
 * no price (`packages/room-mapping-3.0/src/types/dca.ts`, RO-1); a card's `price` is its cheapest visible deal's
 * `totalPrice` and exists for ordering only. `deal.superseded` is never read: card dominance is the dedupe.
 */

// Values match the legacy hotel-page sort (`SORT_OPTIONS` in `HotelPage.react.js`): e2e asserts the
// `room-sort-dropdown` value is `PRICE`.
const RM3_SORT = Object.freeze({
    PRICE: 'PRICE',
    CANCELLATION_DATE: 'CANCELLATION_DATE',
});

const RM3_RIBBON = Object.freeze({
    LOW: 'low',
    VALUE: 'value',
    TERMS: 'terms',
    FC: 'fc',
});

const RM3_RIBBON_PRIORITY = Object.freeze({
    [RM3_RIBBON.TERMS]: 1,
    [RM3_RIBBON.VALUE]: 2,
    [RM3_RIBBON.FC]: 3.5,
    [RM3_RIBBON.LOW]: 7,
});

const RM3_RIBBON_ORDER = Object.freeze(
    Object.values(RM3_RIBBON).sort((a, b) => RM3_RIBBON_PRIORITY[a] - RM3_RIBBON_PRIORITY[b]),
);

// Fixed for every white-label (principal, 2026-10-15): the ribbons are NOT brand-mapped.
const RM3_RIBBON_COLORS = Object.freeze({
    LOW_BACKGROUND: '#008F4B',
    LOW_BORDER: '#008F4B',
    LOW_TEXT: '#FFFFFF',
    LOW_FOLD: '#00663A',
    BEST_BACKGROUND: '#F1F5FB',
    BEST_BORDER: '#3A5A9A',
    BEST_TEXT: '#2F4F8F',
    BEST_FOLD: '#2F4F8F',
});

const RM3_FILTER = Object.freeze({
    TOP_PICKS: 'topPicks',
    FREE_CANCELLATION: 'freeCancellation',
    BOARD: 'board',
    BED: 'bed',
    IN_POLICY: 'inPolicy',
    PAY_AT_HOTEL: 'payAtHotel',
});

const RM3_BED = Object.freeze({
    KING: 'king',
    DOUBLE: 'double',
    TWIN: 'twin',
});

const RM3_DEFAULT_FILTERS = Object.freeze({
    [RM3_FILTER.TOP_PICKS]: Object.freeze([]),
    [RM3_FILTER.FREE_CANCELLATION]: false,
    [RM3_FILTER.BOARD]: null,
    [RM3_FILTER.BED]: null,
    [RM3_FILTER.IN_POLICY]: false,
    [RM3_FILTER.PAY_AT_HOTEL]: false,
});

const RM3_BOARD = Object.freeze({
    ALL_INCLUSIVE: 'all_inclusive',
    FULL_BOARD: 'full_board',
    HALF_BOARD: 'half_board',
    BREAKFAST: 'breakfast',
    ROOM_ONLY: 'room_only',
});

// Board filter chip order, ascending board level. The filter is an exact board match, never "this level or higher".
const RM3_BOARD_ORDER = Object.freeze([
    RM3_BOARD.ROOM_ONLY,
    RM3_BOARD.BREAKFAST,
    RM3_BOARD.HALF_BOARD,
    RM3_BOARD.FULL_BOARD,
    RM3_BOARD.ALL_INCLUSIVE,
]);

const RM3_DETAILS_SOURCE = Object.freeze({
    CATALOG: 'catalog',
    GROUP: 'group',
});

const RM3_SIZE_UNIT = Object.freeze({
    SQUARE_METRES: 'm2',
    SQUARE_FEET: 'ft2',
});

// Untranslated by design (spec §3.6): an internal debug heading, never shown to a customer.
const DEBUG_LABEL_OTHER_ROOMS = 'Other rooms (debug: no RM3 data)';

/**
 * Board precedence, first match wins — `BOARD_PRECEDENCE` in
 * `frontend/web/src/components/pages/hotel/dealViewModel/dealViewModel.js`, which mirrors
 * `DealComponent.getAmenities()`. Level 0 is room only.
 */
const BOARD_PRECEDENCE = [
    ['board_bases.all_inclusive', RM3_BOARD.ALL_INCLUSIVE, 4],
    ['board_bases.full_board', RM3_BOARD.FULL_BOARD, 3],
    ['board_bases.half_board', RM3_BOARD.HALF_BOARD, 2],
    ['breakfast', RM3_BOARD.BREAKFAST, 1],
];
const ROOM_ONLY_LEVEL = 0;

const BED_CHIP_MODES = ['determinate', 'alternatives'];

// Expedia catalog `bed_groups[].configuration[].type` → chip (RM3 owner ruling). Full is a double; Queen and
// SofaBed have no chip.
const CATALOG_BED_TYPE_CHIPS = Object.freeze({
    KingBed: 'king',
    TwinBed: 'twin',
    DoubleBed: 'double',
    FullBed: 'double',
});
const AXIS_BED_CONFIG = 'bed_config';
const AXIS_BED_TYPE = 'bed_type';

const OUTLIER_FACTOR = 2;
const NO_CANCELLATION_SCORE = -1;

// Keep in sync with `BNPL_MIN_DEFERRAL_DAYS` / `BNPL_CHARGE_BUFFER_DAYS_DEFAULT` in `./BnplUtils.js`, which is an
// ES module and cannot be required from this CommonJS one under the unit runner. The test file pins the values.
const BNPL_MIN_DEFERRAL_DAYS = 7;
const BNPL_CHARGE_BUFFER_DAYS_DEFAULT = 6;

const RE_HTML_TAG = /<[^>]*>/g;
const RE_WHITESPACE_RUN = /\s+/g;
const RE_CATALOG_SIZE = /(\d[\d,]*(?:\.\d+)?)\s*sq\.?\s*(meters|metres|feet|ft|m)\b/i;
const RE_UNIT_FEET = /^f/i;

const PATH_DISPLAY_GROUP_KEY = 'room_display_group.key';
const PATH_CARD_ID = 'room_display_group.card_id';
const PATH_LABEL = 'room_display_group.label';
const PATH_CARD_TITLE_ELIGIBLE = 'room_display_group.card_title_eligible';
const PATH_META_CATALOG = 'chooseprod.dca.room_mapping_meta.catalog';

/**
 * A deal's grouping address — non-empty `room_display_group.key` and `card_id` — or null. THE predicate for
 * "this deal goes in a container": `buildRm3Containers` groups by it and `hasRm3Data` requires it.
 */
function rm3AddressOf(deal) {
    const key = nonEmptyString(_.get(deal, PATH_DISPLAY_GROUP_KEY));
    const cardId = nonEmptyString(_.get(deal, PATH_CARD_ID));
    return key && cardId ? { key, cardId } : null;
}

/**
 * §2.2: true iff some deal ran through the engine (`room_options` present) AND has a grouping address, i.e. would
 * land in a container. A deal with `room_options` but no key/card_id must not select the RM3 surface, or the page
 * shows an empty placeholder instead of the legacy list.
 */
function hasRm3Data(deals) {
    return Array.isArray(deals) && deals.some((deal) => _.has(deal, 'room_options') && rm3AddressOf(deal) !== null);
}

function isPresentNumber(value) {
    return value !== '' && value !== null && value !== undefined && Number.isFinite(Number(value));
}

function nonEmptyString(value) {
    const text = typeof value === 'string' ? value.trim() : '';
    return text || null;
}

function totalOf(deal) {
    const value = _.get(deal, 'totalPrice');
    return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
}

function perNightOf(deal) {
    const value = _.get(deal, 'pricePerNight');
    return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
}

/** `compareByPrice` from `dealViewModel.js`: cheapest `totalPrice`, then `pricePerNight`, then `id`. */
function compareByPrice(a, b) {
    const byTotal = totalOf(a) - totalOf(b);
    if (byTotal !== 0 && !Number.isNaN(byTotal)) {
        return byTotal;
    }
    const byNight = perNightOf(a) - perNightOf(b);
    if (byNight !== 0 && !Number.isNaN(byNight)) {
        return byNight;
    }
    return String(a.id || '').localeCompare(String(b.id || ''));
}

/**
 * Card dominance — `isComparable` / `supersededBy` / `visibleDealsOf` from `dealViewModel.js`, i.e. the page's
 * `compareSuperseded` (`Hotel.js`) with `card_id` in place of `room_group`. A deal lacking `board_bases` or
 * `chooseprod.dca` is never compared, so never hidden.
 */
function isComparable(deal) {
    return Boolean(deal && deal.id && deal.board_bases && _.get(deal, 'chooseprod.dca'));
}

function supersededBy(deal, other) {
    if (deal === other || !isComparable(deal) || !isComparable(other)) {
        return false;
    }
    const dealBetter = isDeal1BetterThanDeal2(deal, other);
    const otherBetter = isDeal1BetterThanDeal2(other, deal);
    if (dealBetter && otherBetter) {
        return false;
    }
    if (dealBetter !== otherBetter) {
        return otherBetter;
    }
    return String(deal.id).substring(2) < String(other.id).substring(2);
}

function visibleDealsOf(sorted) {
    return sorted.filter((deal) => !sorted.some((other) => supersededBy(deal, other)));
}

function isNonRefundable(deal) {
    if (deal.chooseprod) {
        return Boolean(_.get(deal, 'chooseprod.dca.nonRefundable'));
    }
    return !deal.refundable;
}

function rawRefundableTill(deal) {
    if (deal.chooseprod) {
        return _.get(deal, 'chooseprod.dca.dealIsFullyRefundableTill') || null;
    }
    return deal.freeCancellationUntil || null;
}

function toIsoOrNull(value) {
    if (!value) {
        return null;
    }
    const parsed = moment.utc(value);
    if (!parsed.isValid()) {
        return null;
    }
    return typeof value === 'string' ? value : parsed.toISOString();
}

function boardOf(deal) {
    const hit = BOARD_PRECEDENCE.find(([path]) => _.get(deal, path));
    return hit ? { key: hit[1], level: hit[2] } : { key: RM3_BOARD.ROOM_ONLY, level: ROOM_ONLY_LEVEL };
}

/**
 * Today's BNPL tag predicate (`DealComponent.getBnplTag`, `BookItem.renderBnplTag`). `account` is the signed-in
 * profile on web and the user on mobile: both carry `settings.bnpl_enabled` and `company.settings`.
 */
function isBnplEligible(deal, account, now) {
    const enabled =
        _.get(account, 'settings.bnpl_enabled', false) || _.get(account, 'company.settings.bnpl_enabled', false);
    if (!enabled) {
        return false;
    }
    if (_.get(deal, 'chooseprod.dca.nonRefundable', true)) {
        return false;
    }
    const cancellationDate = _.get(deal, 'chooseprod.dca.dealIsFullyRefundableTill');
    if (!cancellationDate) {
        return false;
    }
    const settings = _.get(account, 'company.settings');
    const bufferDays = _.get(settings, 'bnpl_charge_buffer_days', BNPL_CHARGE_BUFFER_DAYS_DEFAULT);
    const chargeDate = moment(cancellationDate).subtract(bufferDays, 'days');
    const reference = typeof now === 'number' ? moment(now) : moment();
    if (chargeDate.diff(reference, 'days') < BNPL_MIN_DEFERRAL_DAYS) {
        return false;
    }
    const minimum = _.get(settings, 'bnpl_minimum_booking_amount', 0);
    if (!minimum || minimum <= 0) {
        return true;
    }
    return deal.totalPrice >= minimum;
}

function dealFacts(deal) {
    // A deal with no valid refundable-till date counts as non-refundable (principal 2026-10-19).
    const refundableTill = isNonRefundable(deal) ? null : toIsoOrNull(rawRefundableTill(deal));
    const freeCancellation = refundableTill !== null;
    const board = boardOf(deal);
    return {
        freeCancellation,
        refundableTill,
        boardKey: board.key,
        boardLevel: board.level,
        hasMealPlan: board.level >= 1,
        inPolicy: deal.in_policy === true,
        postPay: !!deal.post_pay,
        bnplEligible: (account, now) => isBnplEligible(deal, account, now),
    };
}

function tillTimeOf(deal) {
    const till = dealFacts(deal).refundableTill;
    return till ? moment.utc(till).valueOf() : null;
}

function makeCard(cardId, deals) {
    const cheapest = deals.reduce((best, deal) => (!best || compareByPrice(deal, best) < 0 ? deal : best), null);
    return {
        cardId,
        deals,
        cheapest,
        price: cheapest ? cheapest.totalPrice : undefined,
        options: Array.isArray(_.get(cheapest, 'room_options')) ? cheapest.room_options : [],
    };
}

function containerDealsOf(container) {
    return _.flatMap(container.cards, (card) => card.deals);
}

function cheapestOfContainer(container) {
    return containerDealsOf(container).reduce(
        (best, deal) => (!best || compareByPrice(deal, best) < 0 ? deal : best),
        null,
    );
}

function stripHtml(text) {
    return String(text || '')
        .replace(RE_HTML_TAG, ' ')
        .replace(RE_WHITESPACE_RUN, ' ')
        .trim();
}

function catalogSizeOf(room) {
    const metres = _.get(room, 'info.room_size.metre_square');
    if (isPresentNumber(metres)) {
        return { value: String(metres), unit: RM3_SIZE_UNIT.SQUARE_METRES };
    }
    const feet = _.get(room, 'info.room_size.square_feet');
    if (isPresentNumber(feet)) {
        return { value: String(feet), unit: RM3_SIZE_UNIT.SQUARE_FEET };
    }
    const match = stripHtml(room.description).match(RE_CATALOG_SIZE);
    if (!match) {
        return null;
    }
    return {
        value: match[1],
        unit: RE_UNIT_FEET.test(match[2]) ? RM3_SIZE_UNIT.SQUARE_FEET : RM3_SIZE_UNIT.SQUARE_METRES,
    };
}

function isPresentId(id) {
    return id !== null && id !== undefined && id !== '';
}

function roomByIdOf(hotel, id) {
    const rooms = _.get(hotel, 'rooms');
    if (!Array.isArray(rooms) || !isPresentId(id)) {
        return null;
    }
    return _.find(rooms, (room) => room && String(room.id) === String(id)) || null;
}

/** §3.3a: exact catalog id only — `room_mapping_meta.catalog.room_id`, else `deal.room_id`. No name fallback. */
function catalogRoomOf(hotel, deal) {
    const metaRoomId = _.get(deal, `${PATH_META_CATALOG}.room_id`);
    return roomByIdOf(hotel, isPresentId(metaRoomId) ? metaRoomId : deal.room_id);
}

// Chips read the deal's own fine-group room first (RM3 owner); the meta id is the inherited display block's room.
function chipRoomOf(hotel, deal) {
    return roomByIdOf(hotel, deal.room_id) || roomByIdOf(hotel, _.get(deal, `${PATH_META_CATALOG}.room_id`));
}

/** Chips from a catalog room's `bed_groups` (map or array); several groups are alternatives, so each one chips. */
function catalogBedChipsOf(room) {
    const groups = _.get(room, 'bed_groups');
    const entries = Array.isArray(groups) ? groups : _.isPlainObject(groups) ? Object.values(groups) : [];
    const chips = new Set();
    entries.forEach((entry) => {
        const beds = Array.isArray(entry) ? entry : _.get(entry, 'configuration');
        (Array.isArray(beds) ? beds : []).forEach((bed) => {
            const chip = CATALOG_BED_TYPE_CHIPS[_.get(bed, 'type')];
            if (chip) {
                chips.add(chip);
            }
        });
    });
    return [...chips];
}

/** The container's catalog bed chips: first visible deal in price order with an exact `chipRoomOf` room. */
function resolveCatalogBedChips(hotel, sortedDeals) {
    for (const deal of sortedDeals) {
        const catalog = chipRoomOf(hotel, deal);
        if (catalog) {
            return Object.freeze(catalogBedChipsOf(catalog));
        }
    }
    return Object.freeze([]);
}

function stringsOf(list, pick) {
    return (Array.isArray(list) ? list : []).map((item) => nonEmptyString(pick(item))).filter(Boolean);
}

const NO_DETAILS = Object.freeze({
    source: null,
    room: null,
    deal: null,
    images: Object.freeze([]),
    amenities: Object.freeze([]),
    size: null,
    sleeps: null,
    bedTypes: Object.freeze([]),
});

/**
 * §3.3a — the container's details carrier: its visible deals in price order across every card, the first one
 * with data wins, and per deal the catalog room wins over `room_group_details` (which may be null). `bedTypes` is
 * the carrier deal's `room_group_details.bedTypes` prose, the bed line's fallback when a card has no bed record;
 * catalog `bed_groups` never feed this bed line (they feed only the bed chips, `catalogBedChips`).
 */
function resolveRm3Details(hotel, sortedDeals) {
    for (const deal of sortedDeals) {
        const catalog = catalogRoomOf(hotel, deal);
        const group = _.isPlainObject(deal.room_group_details) ? deal.room_group_details : null;
        if (!catalog && !group) {
            continue;
        }
        const bedTypes = stringsOf(_.castArray(_.get(group, 'bedTypes', [])), (bed) => bed);
        if (catalog) {
            const total = _.get(catalog, 'occupancy.max_allowed.total');
            return {
                source: RM3_DETAILS_SOURCE.CATALOG,
                room: catalog,
                deal,
                images: stringsOf(catalog.images, (image) => _.get(image, 'original')),
                amenities: stringsOf(catalog.amenities, (amenity) => _.get(amenity, 'name')),
                size: catalogSizeOf(catalog),
                sleeps: isPresentNumber(total) ? Number(total) : null,
                bedTypes,
            };
        }
        return {
            source: RM3_DETAILS_SOURCE.GROUP,
            room: group,
            deal,
            images: stringsOf(group.images, (image) => _.get(image, 'imageUrl')),
            amenities: stringsOf(group.perks, (perk) => perk),
            size: null,
            sleeps: isPresentNumber(group.roomMaxOccupancy) ? Number(group.roomMaxOccupancy) : null,
            bedTypes,
        };
    }
    return NO_DETAILS;
}

function compareContainersByPrice(a, b) {
    const byPrice = compareByPrice(cheapestOfContainer(a) || {}, cheapestOfContainer(b) || {});
    return byPrice !== 0 ? byPrice : String(a.key).localeCompare(String(b.key));
}

function compareCardsByPrice(a, b) {
    const byPrice = compareByPrice(a.cheapest || {}, b.cheapest || {});
    return byPrice !== 0 ? byPrice : String(a.cardId).localeCompare(String(b.cardId));
}

function isHidden(deal, bannedIds) {
    return !deal || deal.not_available || deal.ignore || bannedIds.has(deal.id);
}

/**
 * §3.1. Rebuilt from scratch on every call (partial-recheck `card_id`s are valid within one response only): a
 * `card_id` seen under two keys is two cards. Deals with no grouping address go to `otherRooms`, price-ascending
 * and NOT deduped. `options.bannedDealIds` (web `banned_deal_ids`) are dropped with `not_available` / `ignore`.
 */
function buildRm3Containers(deals, hotel, options) {
    const bannedIds = new Set(_.get(options, 'bannedDealIds') || []);
    const list = (Array.isArray(deals) ? deals : []).filter((deal) => !isHidden(deal, bannedIds));

    const byKey = new Map();
    const others = [];
    list.forEach((deal) => {
        const address = rm3AddressOf(deal);
        if (!address) {
            others.push(deal);
            return;
        }
        const { key, cardId } = address;
        if (!byKey.has(key)) {
            byKey.set(key, new Map());
        }
        const byCard = byKey.get(key);
        if (!byCard.has(cardId)) {
            byCard.set(cardId, []);
        }
        byCard.get(cardId).push(deal);
    });

    const containers = [...byKey.entries()].map(([key, byCard]) => {
        const cards = [...byCard.entries()]
            .map(([cardId, cardDeals]) => makeCard(cardId, visibleDealsOf([...cardDeals].sort(compareByPrice))))
            .sort(compareCardsByPrice);
        const visible = _.flatMap(cards, (card) => card.deals).sort(compareByPrice);
        const label = visible.map((deal) => nonEmptyString(_.get(deal, PATH_LABEL))).find(Boolean) || '';
        const catalogRoomId =
            visible
                .map((deal) => _.get(deal, `${PATH_META_CATALOG}.catalog_room_id`))
                .find((id) => id !== null && id !== undefined && id !== '') || null;
        return {
            key,
            label,
            catalogRoomId,
            cards,
            details: resolveRm3Details(hotel, visible),
            catalogBedChips: resolveCatalogBedChips(hotel, visible),
        };
    });
    containers.sort(compareContainersByPrice);

    return {
        containers,
        otherRooms: others.length ? { deals: others.sort(compareByPrice) } : null,
    };
}

/**
 * Rule (d) of `dealViewModel.buildDealViews`, thin: the sublabel is `room_option_label` (engine title case, §115 /
 * CF-117), else `room_option_description`, of the cheapest visible deal whose `card_title_eligible` is not a present
 * `false`; the title is that sublabel when the container has more than one card, else the container label, else the
 * cheapest deal's `description`.
 */
function cardTitleOf(card, container) {
    const sublabel =
        [...card.deals]
            .sort(compareByPrice)
            .filter((deal) => _.get(deal, PATH_CARD_TITLE_ELIGIBLE) !== false)
            .map((deal) => nonEmptyString(deal.room_option_label) || nonEmptyString(deal.room_option_description))
            .find(Boolean) || '';
    const cardCount = _.get(container, 'cards.length', 0);
    if (cardCount > 1 && sublabel) {
        return sublabel;
    }
    return nonEmptyString(_.get(container, 'label')) || String(_.get(card, 'cheapest.description') || '').trim();
}

function optionRowOf(card, axis) {
    return (Array.isArray(card.options) ? card.options : []).find((row) => row && row.axis === axis) || null;
}

function cardBedOf(card) {
    const config = optionRowOf(card, AXIS_BED_CONFIG);
    const type = optionRowOf(card, AXIS_BED_TYPE);
    if (!config && !type) {
        return null;
    }
    return {
        configValue: config ? nonEmptyString(config.value) : null,
        typeValue: type ? nonEmptyString(type.value) : null,
        bedTypes: config && Array.isArray(config.bed_types) ? [...config.bed_types] : [],
        bedMode: config ? config.bed_mode || null : null,
    };
}

/**
 * Chip source order (RM3 owner): the card's `bed_config` record (determinate/alternatives only), else the
 * container's catalog `bed_groups` (`catalogBedChips`), else none. `bed_type` and bed prose never chip.
 */
function bedChipsOf(card, container) {
    const chips = new Set();
    const config = optionRowOf(card, AXIS_BED_CONFIG);
    if (!config) {
        return new Set(_.get(container, 'catalogBedChips') || []);
    }
    if (!BED_CHIP_MODES.includes(config.bed_mode) || !Array.isArray(config.bed_types)) {
        return chips;
    }
    const known = Object.values(RM3_BED);
    config.bed_types.forEach((token) => {
        if (known.includes(token)) {
            chips.add(token);
        }
    });
    return chips;
}

function participates(deal) {
    return (deal.status === DEAL_STATUS.SUCCESS || !!deal.is_lazy_recheck) && Number.isFinite(deal.totalPrice);
}

function dayKeyOf(till) {
    return moment.utc(till).startOf('day').valueOf();
}

function rankedEntry(deal, containerKey, account, now) {
    const facts = dealFacts(deal);
    const cancellationScore = facts.freeCancellation ? dayKeyOf(facts.refundableTill) : NO_CANCELLATION_SCORE;
    return {
        deal,
        containerKey,
        price: deal.totalPrice,
        freeCancellation: facts.freeCancellation,
        cancellationScore,
        board: facts.boardLevel,
        bnpl: facts.bnplEligible(account, now) ? 1 : 0,
    };
}

// The design's `best()` tie-break: lower price, later free cancellation, higher board, BNPL; then id for stability.
function compareBest(a, b) {
    return (
        a.price - b.price ||
        b.cancellationScore - a.cancellationScore ||
        b.board - a.board ||
        b.bnpl - a.bnpl ||
        String(a.deal.id).localeCompare(String(b.deal.id))
    );
}

function bestOf(entries) {
    return entries.length ? [...entries].sort(compareBest)[0] : null;
}

/**
 * §3.4. Page-wide over every container's visible RM3 deals; call it on the unfiltered, unsplit containers.
 * `options.account` (web profile / mobile user) and `options.now` feed the BNPL dimension; without an account no
 * deal is BNPL-eligible. Rules (principal 2026-10-19), over non-outlier deals; "meal" is breakfast or better:
 * TERMS: best free-cancel meal deal at the latest date, BNPL-only if that pool has any BNPL deal.
 * VALUE: best meal deal, only if a pricier meal deal has strictly better cancellation.
 * FC: best free-cancel deal. LOW: best at the page minimum per container, only if non-refundable.
 */
function assignRibbons(containers, options) {
    const account = _.get(options, 'account');
    const now = _.get(options, 'now');
    const ok = [];
    (Array.isArray(containers) ? containers : []).forEach((container) => {
        const entries = containerDealsOf(container)
            .filter(participates)
            .map((deal) => rankedEntry(deal, container.key, account, now));
        if (!entries.length) {
            return;
        }
        const containerLow = Math.min(...entries.map((entry) => entry.price));
        entries.forEach((entry) => {
            if (entry.price <= OUTLIER_FACTOR * containerLow) {
                ok.push(entry);
            }
        });
    });

    const wins = new Map();
    const win = (entry, ribbon) => {
        if (!entry) {
            return;
        }
        const current = wins.get(entry.deal.id);
        if (!current || RM3_RIBBON_PRIORITY[ribbon] < RM3_RIBBON_PRIORITY[current]) {
            wins.set(entry.deal.id, ribbon);
        }
    };
    if (!ok.length) {
        return wins;
    }

    const freeCancel = ok.filter((entry) => entry.freeCancellation);
    const meal = ok.filter((entry) => entry.board > ROOM_ONLY_LEVEL);
    const termsCandidates = freeCancel.filter((entry) => entry.board > ROOM_ONLY_LEVEL);

    if (termsCandidates.length) {
        const maxC = Math.max(...termsCandidates.map((entry) => entry.cancellationScore));
        const pool = termsCandidates.filter((entry) => entry.cancellationScore === maxC);
        const bnplPool = pool.filter((entry) => entry.bnpl);
        win(bestOf(bnplPool.length ? bnplPool : pool), RM3_RIBBON.TERMS);
    }

    const valueWinner = bestOf(meal);
    if (valueWinner) {
        const betterTermsAbove = meal.some(
            (entry) => entry.price > valueWinner.price && entry.cancellationScore > valueWinner.cancellationScore,
        );
        if (betterTermsAbove) {
            win(valueWinner, RM3_RIBBON.VALUE);
        }
    }

    win(bestOf(freeCancel), RM3_RIBBON.FC);

    const min = Math.min(...ok.map((entry) => entry.price));
    const atMin = ok.filter((entry) => entry.price === min);
    _.uniq(atMin.map((entry) => entry.containerKey)).forEach((key) => {
        const lowWinner = bestOf(atMin.filter((entry) => entry.containerKey === key));
        if (!lowWinner.freeCancellation) {
            win(lowWinner, RM3_RIBBON.LOW);
        }
    });

    return wins;
}

function dealPassesFilters(deal, filters, ribbons) {
    const facts = dealFacts(deal);
    const topPicks = _.get(filters, RM3_FILTER.TOP_PICKS) || [];
    if (topPicks.length && !topPicks.includes(ribbons && ribbons.get(deal.id))) {
        return false;
    }
    if (_.get(filters, RM3_FILTER.FREE_CANCELLATION) && !facts.freeCancellation) {
        return false;
    }
    const board = _.get(filters, RM3_FILTER.BOARD);
    if (board && facts.boardKey !== board) {
        return false;
    }
    if (_.get(filters, RM3_FILTER.IN_POLICY) && !deal.in_policy) {
        return false;
    }
    if (_.get(filters, RM3_FILTER.PAY_AT_HOTEL) && !deal.post_pay) {
        return false;
    }
    return true;
}

function keepDeals(containers, keep) {
    return containers
        .map((container) => {
            const cards = container.cards
                .map((card) => {
                    const deals = card.deals.filter(keep);
                    return deals.length ? { ...card, ...makeCard(card.cardId, deals) } : null;
                })
                .filter(Boolean);
            return cards.length ? { ...container, cards } : null;
        })
        .filter(Boolean);
}

/**
 * §3.5. Deal-level filters drop deals, an empty card is dropped, an empty container is dropped; the bed filter is
 * card-level. `otherRooms` takes every user filter except bed, and any bed filter hides it.
 */
function applyRm3Filters(containers, otherRooms, filters, ribbons) {
    const bed = _.get(filters, RM3_FILTER.BED);
    const keep = (deal) => dealPassesFilters(deal, filters, ribbons);

    const byBed = bed
        ? (containers || [])
              .map((container) => {
                  const cards = container.cards.filter((card) => bedChipsOf(card, container).has(bed));
                  return cards.length ? { ...container, cards } : null;
              })
              .filter(Boolean)
        : containers || [];

    let filteredOthers = null;
    if (otherRooms && !bed) {
        const deals = otherRooms.deals.filter(keep);
        filteredOthers = deals.length ? { ...otherRooms, deals } : null;
    }

    return { containers: keepDeals(byBed, keep), otherRooms: filteredOthers };
}

function filtersWithOnly(key, value) {
    return { ...RM3_DEFAULT_FILTERS, [RM3_FILTER.TOP_PICKS]: [], [key]: value };
}

/**
 * Which filter chips can match: each chip is applied ALONE to the unfiltered containers (other chips off), and is
 * available iff at least one deal survives, so a board chip exists only for a board some deal has. Shape mirrors the
 * filter state; `otherRooms` count only when included.
 */
function availableFilters(containers, otherRooms, ribbons, options) {
    const others = _.get(options, 'includeOtherRooms') ? otherRooms : null;
    const matches = (key, value) => {
        const result = applyRm3Filters(containers, others, filtersWithOnly(key, value), ribbons);
        return result.containers.length > 0 || result.otherRooms !== null;
    };
    const byValue = (key, values) => Object.fromEntries(values.map((value) => [value, matches(key, value)]));
    return {
        [RM3_FILTER.TOP_PICKS]: Object.fromEntries(
            RM3_RIBBON_ORDER.map((kind) => [kind, matches(RM3_FILTER.TOP_PICKS, [kind])]),
        ),
        [RM3_FILTER.FREE_CANCELLATION]: matches(RM3_FILTER.FREE_CANCELLATION, true),
        [RM3_FILTER.BOARD]: byValue(RM3_FILTER.BOARD, RM3_BOARD_ORDER),
        [RM3_FILTER.BED]: byValue(RM3_FILTER.BED, Object.values(RM3_BED)),
        [RM3_FILTER.IN_POLICY]: matches(RM3_FILTER.IN_POLICY, true),
        [RM3_FILTER.PAY_AT_HOTEL]: matches(RM3_FILTER.PAY_AT_HOTEL, true),
    };
}

/**
 * §3.7. Payment timing is classified per deal, so a container (or card) holding both kinds is cut into a pre-pay
 * copy and a post-pay copy, each holding only its own kind. `multipleKinds` drives today's subtitles.
 */
function splitByPayment(containers) {
    const list = containers || [];
    const prePay = keepDeals(list, (deal) => !deal.post_pay);
    const postPay = keepDeals(list, (deal) => !!deal.post_pay);
    return { prePay, postPay, multipleKinds: prePay.length > 0 && postPay.length > 0 };
}

function latestTillOf(deals) {
    const times = deals.map(tillTimeOf).filter((time) => time !== null);
    return times.length ? Math.max(...times) : null;
}

function compareByLatestTill(aTill, bTill) {
    if (aTill === bTill) {
        return 0;
    }
    if (aTill === null) {
        return 1;
    }
    if (bTill === null) {
        return -1;
    }
    return bTill - aTill;
}

function compareDealsByCancellation(a, b) {
    return compareByLatestTill(tillTimeOf(a), tillTimeOf(b)) || compareByPrice(a, b);
}

/** §3.5, applied to one payment section: containers, the cards inside each, and the deals inside each card. */
function sortRm3Containers(containers, sort) {
    const byCancellation = sort === RM3_SORT.CANCELLATION_DATE;
    const sortedContainers = (containers || []).map((container) => {
        const cards = container.cards.map((card) => ({
            ...card,
            deals: [...card.deals].sort(byCancellation ? compareDealsByCancellation : compareByPrice),
        }));
        cards.sort((a, b) =>
            byCancellation
                ? compareByLatestTill(latestTillOf(a.deals), latestTillOf(b.deals)) || compareCardsByPrice(a, b)
                : compareCardsByPrice(a, b),
        );
        return { ...container, cards };
    });
    sortedContainers.sort((a, b) =>
        byCancellation
            ? compareByLatestTill(latestTillOf(containerDealsOf(a)), latestTillOf(containerDealsOf(b))) ||
              compareContainersByPrice(a, b)
            : compareContainersByPrice(a, b),
    );
    return sortedContainers;
}

module.exports = {
    RM3_SORT,
    RM3_RIBBON,
    RM3_RIBBON_PRIORITY,
    RM3_RIBBON_ORDER,
    RM3_RIBBON_COLORS,
    RM3_FILTER,
    RM3_BED,
    RM3_DEFAULT_FILTERS,
    RM3_BOARD,
    RM3_BOARD_ORDER,
    RM3_DETAILS_SOURCE,
    RM3_SIZE_UNIT,
    DEBUG_LABEL_OTHER_ROOMS,
    hasRm3Data,
    buildRm3Containers,
    cardTitleOf,
    cardBedOf,
    bedChipsOf,
    dealFacts,
    assignRibbons,
    applyRm3Filters,
    availableFilters,
    splitByPayment,
    sortRm3Containers,
    compareByPrice,
};
